import { useDataModel } from '@thd-nucleus/data-sources';
import {
  activeOrderFilterOptionsDataModel,
  ActiveOrderFilterOptionsQueryVariables
} from './activeOrderFilterOptionsDataModel';
import { RawActiveOrderFilterOptionsResponse } from './types';
import { QueryResult, toQueryResult } from './queryResult';

// Project/PO-Job Name facet option lists for ActiveOrdersTable, sourced from their own dedicated
// activeOrdersFilters query — never derived from the currently-loaded page's own
// activeDistributions — mirroring useOrderFilterOptionsQuery.ts's precedent for AllOrdersTable's
// PO/Job Name and Payment options (that one stays a plain `fetch`, since no GraphQL equivalent has
// been confirmed for it; this one moved onto @thd-nucleus/data-sources once activeOrdersFilters was
// confirmed against order-management-svc's schema.graphqls — see activeOrderFilterOptionsDataModel.ts
// for the full contract and citation).
export interface ActiveOrderFilterOptionsVariables {
  purchaserId: string;
  customerAccountId: string;
}

export interface ActiveOrderFilterOptions {
  project: string[];
  poJobName: string[];
}

export interface UseActiveOrderFilterOptionsQueryOptions {
  skip?: boolean;
}

const EMPTY_OPTIONS: ActiveOrderFilterOptions = { project: [], poJobName: [] };

export function useActiveOrderFilterOptionsQuery(
  variables: ActiveOrderFilterOptionsVariables,
  { skip = false }: UseActiveOrderFilterOptionsQueryOptions = {}
): QueryResult<ActiveOrderFilterOptions> {
  const { purchaserId, customerAccountId } = variables;

  // purchaserId is sent as the query's top-level `userId` argument — the same purchaserId-as-
  // identity assumption ADR-003 documents for the sibling activeOrders query. distributionDate is
  // REQUIRED at runtime by ActiveOrdersFilterRequestValidator (see
  // activeOrderFilterOptionsDataModel.ts); sent as 'ALL' since these options are a global facet
  // universe, not scoped to the table's selected date range.
  const queryVariables: ActiveOrderFilterOptionsQueryVariables = {
    userId: purchaserId,
    request: {
      customerAccountId,
      distributionDate: 'ALL',
      filterTypes: ['project', 'poJobName']
    }
  };

  const response = useDataModel('activeOrdersFilters', {
    variables: queryVariables,
    dataModel: activeOrderFilterOptionsDataModel,
    skip,
    context: { withAuth: true }
  });

  // useDataModel returns Apollo's raw useQuery response, keyed by query name
  // (response.data === { activeOrdersFilters: RawActiveOrderFilterOptionsResponse }) — unwrap it
  // and flatten the ActiveOrdersFilterResponseWrapper -> ActiveOrdersFilterResponse nesting so every
  // caller of this hook works with the flat { project, poJobName } shape directly.
  //
  // Success/loading is keyed off `rawResponse` (the activeOrdersFilters field itself resolving),
  // NOT the nested `activeOrdersFilterResponse` — that inner field is nullable in the schema, so a
  // real, successful "no filters for this account" response resolves it to `null`. Keying off it
  // instead would misreport that legitimate empty case as perpetual 'loading' — same one-level-unwrap
  // convention useActiveOrdersQuery.ts uses for response.data?.activeOrders.
  const rawResponse: RawActiveOrderFilterOptionsResponse | undefined = response.data?.activeOrdersFilters;

  return toQueryResult<ActiveOrderFilterOptions>(
    {
      ...response,
      data: rawResponse
        ? {
            project: rawResponse.activeOrdersFilterResponse?.project ?? [],
            poJobName: rawResponse.activeOrdersFilterResponse?.poJobName ?? []
          }
        : undefined
    },
    skip
  );
}

export { EMPTY_OPTIONS as EMPTY_ACTIVE_ORDER_FILTER_OPTIONS };
